/**
 * The FuzzyDate value object: parse, validate, compare, format.
 *
 * NORMATIVE SOURCE: docs/02-domain-model.md §4 and ADR-0004. The database stores
 * every historical date as the six-column group `{p}_year/_month/_day/_qualifier/
 * _text/_sort_key`; this module is the one place that folds that group into a
 * single object and back, so neither the api nor the SPA ever spells a column
 * prefix by hand.
 *
 * `formatFuzzyDate` is a character-for-character port of the SQL
 * `fuzzy_date_display()` of migrations/1786838400001_helper-functions.sql, minus
 * its `'date unknown'` fallback: an unknown date is `null` here, because the
 * label for it is a locale string and belongs to the SPA's catalogue.
 */
import { DATE_QUALIFIER } from './enums.js';
/** Year bounds of the CHECK constraints (docs/04 §5.2). */
export const FUZZY_YEAR_MIN = 1;
export const FUZZY_YEAR_MAX = 2200;
/** The column-group prefixes in use across the schema (ADR-0004). */
export const FUZZY_DATE_PREFIXES = ['birth', 'death', 'taken', 'started', 'ended'];
/* --------------------------------------------------------------- predicates */
/** True when nothing at all is recorded — the legal "unknown date" (§4). */
export function isFuzzyDateEmpty(date) {
    if (!date)
        return true;
    return (date.year == null &&
        date.month == null &&
        date.day == null &&
        date.qualifier == null &&
        (date.text == null || date.text.trim() === ''));
}
/**
 * The generated column, in TypeScript: `year*10000 + month*100 + day`, null iff
 * the year is null. Used to predict a sort key before the row exists (forms,
 * fixtures, tests); the database value always wins once written.
 */
export function fuzzyDateSortKey(date) {
    if (!date || date.year == null)
        return null;
    return date.year * 10000 + (date.month ?? 0) * 100 + (date.day ?? 0);
}
/**
 * Port of SQL `fuzzy_date_not_after()`: is `a` no later than `b`, compared at the
 * precision the two of them share? Unknown components make the comparison
 * inconclusive, which counts as "not after" — the database CHECK is deliberately
 * permissive rather than inventing precision it does not have (§2.3).
 */
export function fuzzyDateNotAfter(a, b) {
    const y1 = a?.year ?? null;
    const y2 = b?.year ?? null;
    if (y1 == null || y2 == null)
        return true;
    if (y1 !== y2)
        return y1 < y2;
    const m1 = a?.month ?? null;
    const m2 = b?.month ?? null;
    if (m1 == null || m2 == null)
        return true;
    if (m1 !== m2)
        return m1 < m2;
    const d1 = a?.day ?? null;
    const d2 = b?.day ?? null;
    if (d1 == null || d2 == null)
        return true;
    return d1 <= d2;
}
/**
 * Comparator for chronological lists: by sort key, **nulls last** (§4), matching
 * the `NULLS LAST` ordering the api asks Postgres for.
 */
export function compareFuzzyDates(a, b) {
    const ka = fuzzyDateSortKey(a);
    const kb = fuzzyDateSortKey(b);
    if (ka == null && kb == null)
        return 0;
    if (ka == null)
        return 1;
    if (kb == null)
        return -1;
    return ka - kb;
}
/**
 * Every rule the column group is subject to, checked in one pass so a form can
 * show them all at once. The first six mirror CHECK constraints; `day_not_in_month`
 * is app-level only — the schema bounds the day at 1..31 without knowing the month,
 * and 31 February should never reach it.
 */
export function validateFuzzyDate(date) {
    const problems = [];
    if (!date)
        return problems;
    const { year = null, month = null, day = null, qualifier = null } = date;
    if (year != null && (!Number.isInteger(year) || year < FUZZY_YEAR_MIN || year > FUZZY_YEAR_MAX)) {
        problems.push('year_out_of_range');
    }
    if (month != null && (!Number.isInteger(month) || month < 1 || month > 12)) {
        problems.push('month_out_of_range');
    }
    if (day != null && (!Number.isInteger(day) || day < 1 || day > 31)) {
        problems.push('day_out_of_range');
    }
    if (month != null && year == null)
        problems.push('month_requires_year');
    if (day != null && month == null)
        problems.push('day_requires_month');
    if (qualifier != null && year == null)
        problems.push('qualifier_requires_year');
    if (qualifier != null && !DATE_QUALIFIER.includes(qualifier)) {
        problems.push('unknown_qualifier');
    }
    if (year != null &&
        month != null &&
        day != null &&
        !problems.includes('year_out_of_range') &&
        !problems.includes('month_out_of_range') &&
        !problems.includes('day_out_of_range') &&
        day > daysInMonth(year, month)) {
        problems.push('day_not_in_month');
    }
    return problems;
}
/** Proleptic-Gregorian month length; the calendar Postgres itself uses. */
export function daysInMonth(year, month) {
    if (month === 2) {
        const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        return leap ? 29 : 28;
    }
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
/* ---------------------------------------------------------------- formatting */
const QUALIFIER_PREFIX = {
    exact: '',
    about: '~',
    before: '<',
    after: '>',
    estimated: 'est. ',
};
/**
 * The display form of §4: `1943`, `1943-05`, `1943-05-17`, prefixed `~`/`<`/`>`/
 * `est. `; the verbatim `text` when no year is known; `null` when nothing is.
 */
export function formatFuzzyDate(date) {
    if (!date)
        return null;
    const { year = null, month = null, day = null } = date;
    if (year != null) {
        const prefix = QUALIFIER_PREFIX[date.qualifier ?? 'exact'] ?? '';
        let out = `${prefix}${year}`;
        if (month != null) {
            out += `-${pad2(month)}`;
            if (day != null)
                out += `-${pad2(day)}`;
        }
        return out;
    }
    const text = date.text?.trim();
    return text ? text : null;
}
/**
 * Year-only rendering for dense contexts (list rows, tree nodes): `1943`,
 * `~1943`, or the verbatim text truncated by the caller. Null when unknown.
 */
export function formatFuzzyYear(date) {
    if (!date)
        return null;
    if (date.year == null) {
        const text = date.text?.trim();
        return text ? text : null;
    }
    return `${QUALIFIER_PREFIX[date.qualifier ?? 'exact'] ?? ''}${date.year}`;
}
/**
 * The `1943–2001` lifespan form used on profiles and tree nodes. Returns null
 * when neither end is known; an open end renders as `1943–` / `–2001`.
 */
export function formatLifespan(birth, death) {
    const b = formatFuzzyYear(birth);
    const d = formatFuzzyYear(death);
    if (b == null && d == null)
        return null;
    return `${b ?? ''}–${d ?? ''}`;
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
/* ------------------------------------------------------------------ parsing */
/**
 * Read one of the display forms back: `1943`, `1943-05`, `1943-05-17`, each
 * optionally prefixed `~`, `<`, `>`, `est.`/`est `, plus the bare word forms
 * `about`/`c.`/`ca.`/`circa`, `before`, `after`, `estimated`.
 *
 * Returns `null` for anything else, which is not a failure: the caller stores
 * the wording verbatim in `text` (§4), where "before the war" belongs.
 * A parse that succeeds is still run through `validateFuzzyDate` by the caller —
 * `2026-02-31` parses and then fails validation, which is the honest split.
 */
export function parseFuzzyDate(input) {
    const raw = input.trim();
    if (raw === '')
        return null;
    const { qualifier, rest } = stripQualifier(raw);
    const match = /^(\d{1,4})(?:[-/](\d{1,2})(?:[-/](\d{1,2}))?)?$/.exec(rest.trim());
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = match[2] == null ? null : Number(match[2]);
    const day = match[3] == null ? null : Number(match[3]);
    if (year < FUZZY_YEAR_MIN || year > FUZZY_YEAR_MAX)
        return null;
    return { year, month, day, qualifier, text: null };
}
function stripQualifier(raw) {
    const lower = raw.toLowerCase();
    for (const [prefix, qualifier] of TEXT_QUALIFIERS) {
        if (lower.startsWith(prefix))
            return { qualifier, rest: raw.slice(prefix.length) };
    }
    const symbol = raw[0];
    if (symbol === '~')
        return { qualifier: 'about', rest: raw.slice(1) };
    if (symbol === '<')
        return { qualifier: 'before', rest: raw.slice(1) };
    if (symbol === '>')
        return { qualifier: 'after', rest: raw.slice(1) };
    return { qualifier: 'exact', rest: raw };
}
/** Longest first, so `circa` is not shadowed by `c.`. */
const TEXT_QUALIFIERS = [
    ['estimated ', 'estimated'],
    ['circa ', 'about'],
    ['about ', 'about'],
    ['before ', 'before'],
    ['after ', 'after'],
    ['est. ', 'estimated'],
    ['est ', 'estimated'],
    ['ca. ', 'about'],
    ['ca ', 'about'],
    ['c. ', 'about'],
];
/**
 * Fold a row's six columns into one object, or `null` when the date is unknown —
 * the JSON shape of docs/05 §1.3, where `"death": null` means "nothing recorded".
 */
export function fuzzyDateFromColumns(row, prefix) {
    const read = (suffix) => row[`${prefix}_${suffix}`];
    const parts = {
        year: read('year') ?? null,
        month: read('month') ?? null,
        day: read('day') ?? null,
        qualifier: read('qualifier') ?? null,
        text: read('text') ?? null,
        sort_key: read('sort_key') ?? null,
    };
    return isFuzzyDateEmpty(parts) ? null : parts;
}
/**
 * Unfold an object into the five writable columns (never `_sort_key`, which is
 * `GENERATED ALWAYS`). A null/absent date clears the whole group, so an edit that
 * removes a date does not leave a stale month behind.
 */
export function fuzzyDateToColumns(date, prefix) {
    const empty = isFuzzyDateEmpty(date);
    const text = date?.text?.trim();
    return {
        [`${prefix}_year`]: empty ? null : (date?.year ?? null),
        [`${prefix}_month`]: empty ? null : (date?.month ?? null),
        [`${prefix}_day`]: empty ? null : (date?.day ?? null),
        // The column defaults to `exact` in spirit but is nullable in fact, and a
        // qualifier without a year violates its CHECK — so it rides with the year.
        [`${prefix}_qualifier`]: empty || date?.year == null ? null : (date?.qualifier ?? 'exact'),
        [`${prefix}_text`]: empty || !text ? null : text,
    };
}
/* ------------------------------------------------------- dates told relatively */
/**
 * How a date was stated in relation to another event.
 *
 * Families do not remember dates; they remember order. "She died two years after
 * her brother", "he was born the same month as his cousin", "just after they
 * married" — this is the ordinary shape of the evidence, and until now the only
 * place to put it was the verbatim `text` field, where nothing could sort it,
 * filter it or reason about it.
 */
export const RELATIVE_DATE_RELATIONS = [
    'same_year',
    'same_month',
    'years_after',
    'years_before',
    'just_after',
    'just_before',
];
/**
 * Turn a relative statement into a fuzzy date.
 *
 * **This resolves once, at entry.** The result is an ordinary fuzzy date; it does
 * not stay tied to the anchor, so correcting the anchor later does not move it.
 * That is a real limitation and the reason `text` always carries the original
 * wording — the testimony outlives the arithmetic, and a reader can always see
 * what was actually said. ADR-0008 records the anchored model that would fix it
 * properly, and why it is not v1.
 *
 * Returns null when the anchor has no year: "two years after" an unknown date is
 * not a date, and inventing one would be worse than the `text` field alone.
 */
export function deriveRelativeDate(input) {
    const anchorYear = input.anchor?.year;
    if (anchorYear == null) {
        // Nothing computable — but the wording is still worth keeping.
        return input.wording ? { text: input.wording } : null;
    }
    const text = input.wording ?? undefined;
    const offset = Math.trunc(input.offsetYears ?? 0);
    switch (input.relation) {
        case 'same_year':
            return { year: anchorYear, qualifier: 'exact', ...(text ? { text } : {}) };
        case 'same_month':
            // Only meaningful when the anchor names a month; otherwise it is the year.
            return input.anchor?.month != null
                ? {
                    year: anchorYear,
                    month: input.anchor.month,
                    qualifier: 'exact',
                    ...(text ? { text } : {}),
                }
                : { year: anchorYear, qualifier: 'exact', ...(text ? { text } : {}) };
        case 'years_after':
        case 'years_before': {
            const delta = input.relation === 'years_after' ? offset : -offset;
            const year = anchorYear + delta;
            if (year < FUZZY_YEAR_MIN || year > FUZZY_YEAR_MAX)
                return text ? { text } : null;
            // "About": counting years from a remembered event is an estimate, and the
            // qualifier is where that honesty lives rather than in a footnote.
            return { year, qualifier: 'about', ...(text ? { text } : {}) };
        }
        case 'just_after':
            // No offset was given, so the only firm claim is the ordering itself —
            // which is exactly what the `after` qualifier means (ADR-0004).
            return { year: anchorYear, qualifier: 'after', ...(text ? { text } : {}) };
        case 'just_before':
            return { year: anchorYear, qualifier: 'before', ...(text ? { text } : {}) };
    }
}
/** The English wording a relative statement produces, for the verbatim `text`. */
export function describeRelativeDate(relation, anchorDescription, offsetYears) {
    const years = Math.trunc(offsetYears ?? 0);
    const plural = years === 1 ? 'year' : 'years';
    switch (relation) {
        case 'same_year':
            return `the same year as ${anchorDescription}`;
        case 'same_month':
            return `the same month as ${anchorDescription}`;
        case 'years_after':
            return `${String(years)} ${plural} after ${anchorDescription}`;
        case 'years_before':
            return `${String(years)} ${plural} before ${anchorDescription}`;
        case 'just_after':
            return `just after ${anchorDescription}`;
        case 'just_before':
            return `just before ${anchorDescription}`;
    }
}
//# sourceMappingURL=fuzzy-date.js.map